import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Check, Palette, Link2, Music, Eye, Shield, Zap } from "lucide-react";

const FEATURES = [
  { icon: Palette, title: "Full Customization", desc: "Backgrounds, banners, avatar shapes, colors and badges — make it unmistakably yours." },
  { icon: Link2, title: "Unlimited Links", desc: "Showcase every social, store and project with animated, glowing link buttons." },
  { icon: Music, title: "Background Audio", desc: "Set the mood with background music that plays when visitors land on your page." },
  { icon: Eye, title: "View Analytics", desc: "Track exactly how many people have visited your bio page in real time." },
  { icon: Shield, title: "Invite Only", desc: "An exclusive community. Registration requires a valid invite code from an admin." },
  { icon: Zap, title: "1-Char Usernames", desc: "Claim ultra-rare single character usernames before anyone else." },
];

export default function Landing() {
  const [username, setUsername] = useState("");
  const [status, setStatus] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const debRef = useRef(null);

  useEffect(() => {
    if (!username) { setStatus(null); return; }
    clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/check-username/${encodeURIComponent(username)}`);
        setStatus(data);
      } catch {
        setStatus(null);
      }
    }, 350);
    return () => clearTimeout(debRef.current);
  }, [username]);

  const handleClaim = () => {
    if (!username) return;
    if (status && status.available) {
      navigate(`/register?u=${encodeURIComponent(username)}`);
    } else if (status && !status.available) {
      toast.error("That username is already taken.");
    }
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <div className="absolute inset-0 radial-violet pointer-events-none" />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-20 px-4 max-w-4xl mx-auto text-center">
        <div className="animate-fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#22222B] bg-[#111115] text-xs text-zinc-400 font-mono uppercase tracking-widest mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-pulse" /> Invite only
          </div>
          <h1 className="font-serif font-bold tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-grad">
            Your digital identity,
            <br />
            simplified.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-zinc-400 max-w-xl mx-auto leading-relaxed">
            Create stunning bio links, showcase your content, and connect with your audience.
            grime.bio gives you the tools to build your online presence — beautifully.
          </p>
        </div>

        {/* Claim box */}
        <div className="animate-fade-up mt-10 max-w-md mx-auto" style={{ animationDelay: "0.15s" }}>
          <div className="relative flex items-center bg-[#111115] border border-[#22222B] focus-within:border-violet-500/60 rounded-full p-1.5 shadow-2xl transition-all">
            <span className="pl-4 pr-1 font-mono text-sm text-zinc-500 select-none">grime.bio/</span>
            <input
              data-testid="claim-username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_.-]/g, ""))}
              onKeyDown={(e) => e.key === "Enter" && handleClaim()}
              placeholder="username"
              maxLength={30}
              className="flex-1 bg-transparent outline-none text-white placeholder:text-zinc-600 text-sm min-w-0"
            />
            {username && status && (
              <span className="px-2">
                {status.available ? (
                  <Check className="w-4 h-4 text-emerald-400" data-testid="username-available" />
                ) : (
                  <span className="text-xs text-rose-400 font-mono" data-testid="username-taken">taken</span>
                )}
              </span>
            )}
            <button
              data-testid="claim-btn"
              onClick={handleClaim}
              className="flex items-center gap-1.5 bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium rounded-full px-5 py-2 transition-colors shadow-[0_0_20px_rgba(139,92,246,0.4)]"
            >
              Claim <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-zinc-600 mt-3">Single-character usernames supported. Requires an invite code.</p>
        </div>

        {/* Mockup */}
        <div className="animate-fade-up mt-16" style={{ animationDelay: "0.3s" }}>
          <div className="relative mx-auto max-w-3xl rounded-2xl border border-zinc-800 bg-[#0d0d11]/80 backdrop-blur-xl p-3 shadow-[0_0_80px_rgba(139,92,246,0.15)]">
            <div className="rounded-xl overflow-hidden border border-zinc-800/80 bg-[#0a0a0c] aspect-[16/9] flex items-center justify-center">
              <div className="grid grid-cols-3 w-full h-full">
                <div className="border-r border-zinc-800/60 p-4 space-y-2 hidden sm:block">
                  {["Overview", "Appearance", "Links", "Badges", "Audio", "Analytics"].map((t, i) => (
                    <div key={t} className={`text-xs px-2 py-1.5 rounded-md ${i === 1 ? "bg-violet-600/20 text-violet-300" : "text-zinc-500"}`}>{t}</div>
                  ))}
                </div>
                <div className="col-span-2 sm:col-span-1 p-4 space-y-3">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-rose-400 to-pink-500" />
                  <div className="h-2 w-24 rounded bg-zinc-700" />
                  <div className="h-2 w-16 rounded bg-zinc-800" />
                  <div className="h-8 rounded-lg bg-violet-600/80 mt-4" />
                  <div className="h-20 rounded-lg border border-dashed border-zinc-700" />
                </div>
                <div className="p-4 flex-col items-center justify-center hidden sm:flex bg-gradient-to-b from-violet-900/20 to-transparent">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-rose-400 to-pink-500 mb-2" />
                  <div className="h-2 w-12 rounded bg-zinc-600 mb-3" />
                  <div className="flex gap-1.5">
                    {[0, 1, 2].map((i) => <div key={i} className="w-6 h-6 rounded-full bg-zinc-800" />)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative px-4 py-20 max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-violet-400 mb-3">Everything you need</p>
          <h2 className="font-serif font-semibold text-3xl sm:text-4xl text-white">Built to stand out.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              data-testid={`feature-${f.title.toLowerCase().replace(/\s/g, "-")}`}
              className="group rounded-2xl border border-[#22222B] bg-[#111115] p-6 hover:border-violet-500/40 hover:bg-[#15151b] transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-violet-600/15 border border-violet-500/20 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                <f.icon className="w-5 h-5 text-violet-400" />
              </div>
              <h3 className="text-lg font-medium text-zinc-100 mb-1.5">{f.title}</h3>
              <p className="text-sm text-zinc-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section id="premium" className="relative px-4 py-20 max-w-3xl mx-auto text-center">
        <div className="rounded-3xl border border-[#22222B] bg-gradient-to-b from-violet-900/15 to-[#111115] p-12">
          <h2 className="font-serif font-semibold text-3xl sm:text-4xl text-white mb-4">Claim your name today.</h2>
          <p className="text-zinc-400 mb-8 max-w-md mx-auto">Join an exclusive, invite-only community of creators. Your perfect bio link is one code away.</p>
          <button
            data-testid="cta-register-btn"
            onClick={() => navigate(user ? "/dashboard" : "/register")}
            className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white font-medium rounded-full px-7 py-3 transition-colors shadow-[0_0_30px_rgba(139,92,246,0.5)]"
          >
            {user ? "Go to Dashboard" : "Get Started"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <footer id="discord" className="relative border-t border-[#18181f] px-4 py-8 text-center text-sm text-zinc-600">
        grime.bio — your digital identity, simplified.
      </footer>
    </div>
  );
}
